import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, Input, Spinner, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/formatters';

export function Register() {
  const { slug, eventId } = useParams<{ slug: string; eventId: string }>();
  const nav = useNavigate();
  const toast = useToast();

  const { data: ev, isLoading } = useQuery({
    queryKey: ['publicEvent', slug, eventId],
    queryFn: async () => (await api.get(`/public/${slug}/events/${eventId}`)).data,
    enabled: !!slug && !!eventId,
  });

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    smsConsent: false,
    smsConsentText:
      'I agree to receive automated event reminders and notifications at the phone number provided. Msg & data rates may apply. Reply STOP to unsubscribe.',
    groupSize: 1,
  });

  const register = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        eventId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        groupSize: form.groupSize,
        smsConsent: form.smsConsent,
      };
      if (form.phone) body.phone = form.phone;
      if (form.smsConsent) body.smsConsentText = form.smsConsentText;
      return (await api.post(`/public/events/${slug}/register`, body)).data;
    },
    onSuccess: (result) => {
      if (result.kind === 'REGISTERED') {
        nav(`/${slug}/events/${eventId}/registered`);
      } else {
        toast.push('Added to the waitlist', 'info');
        nav(`/${slug}/events/${eventId}`);
      }
    },
    onError: (err) => toast.push(errorMessage(err), 'error'),
  });

  if (isLoading) return <Spinner />;
  if (!ev) return <div className="p-6">Event not found.</div>;

  return (
    <div className="min-h-screen bg-cream-100 px-6 py-10">
      <div className="max-w-lg mx-auto space-y-4">
        <h1 className="font-display text-3xl">Register for {ev.event.title}</h1>
        <p className="text-ink-muted">{formatDateTime(ev.event.startTime)}</p>

        <Card className="p-5">
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              register.mutate();
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="First name"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                required
              />
              <Input
                label="Last name"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
              />
            </div>
            <Input
              type="email"
              label="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <Input
              type="tel"
              label="Phone (E.164, e.g. +19195551234)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+19195551234"
            />

            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={form.smsConsent}
                onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })}
                className="mt-1"
              />
              <span>{form.smsConsentText}</span>
            </label>

            <Button type="submit" loading={register.isPending} fullWidth>
              Register
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
